import { isNil } from "lodash";
import React, { useEffect, useState } from "react";

import { Dialog, SelectItem, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  CaregiverInput,
  UpdateLearnerCaregiverDetailsMutation,
  useUpdateLearnerCaregiverDetailsMutation,
} from "@api/graphql/types-and-hooks";
import { DoubleColumnContainer, FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import {
  IsAllowedAppAccessItem,
  NEAREST_LOCATIONS_OPTIONS,
  PrimaryCaregiverItem,
  REFERRAL_SOURCE_OPTIONS,
  SMSAgreementItem,
} from "@pages/NewClient/NewClientOnboarding/NewClientOnboarding.interfaces";
import { useAdminData } from "@providers/AdminDataProvider";
import { useClientData } from "@providers/ClientProvider";

export type caregiverListType = CaregiverInput & {
  intakeInvite?: string | null;
  invitationUrl?: string | null;
  auth0Id?: string | null;
  consentGiven?: boolean;
  hasEnteredVideoCaptureApp?: boolean;
};

interface AddUpdateCaregiverDialogueProps {
  setDialogOpen: (open: boolean) => void;
  isDialogOpen: boolean;
  onAddCaregiver?: (caregiver: CaregiverInput) => void;
  selectedCaregiver?: caregiverListType | null;
  clientId?: string;
  isPrimaryCaregiver?: boolean | null;
  assignedCaregivers: caregiverListType[];
  // "quickAdd" hides the app-access / mark-as-primary controls for the New Client
  // creation flow, where they don't apply yet (only one caregiver at a time exists).
  variant?: "quickAdd" | "full";
}

type AdditionalProperty = {
  isExistingCaregiver: string;
};
export const AddUpdateCaregiverDialogue: React.FC<AddUpdateCaregiverDialogueProps> = ({
  isDialogOpen,
  setDialogOpen,
  onAddCaregiver,
  selectedCaregiver,
  clientId,
  isPrimaryCaregiver,
  assignedCaregivers,
  variant = "full",
}) => {
  const { isOnboardingEnabled } = useAdminData();
  const { caregivers, setCaregivers, isCaregiverLoading } = useClientData();
  const [caregiver, setCaregiver] = useState<CaregiverInput | null>(null);

  const updateLearnerCaregiverDetailsMutation = useUpdateLearnerCaregiverDetailsMutation({});

  const syncCaregiversAfterUpdate = (res: UpdateLearnerCaregiverDetailsMutation, rawParams: CaregiverInput) => {
    if (!res.updateLearnerCaregiverDetails?.status) return;

    if (selectedCaregiver) {
      setCaregivers((prev) =>
        prev.map((caregiver) =>
          caregiver.id === rawParams.caregiverId || caregiver.id === selectedCaregiver?.caregiverId
            ? {
                ...caregiver,
                caregivermetadata: {
                  ...caregiver.caregivermetadata,
                  firstName: rawParams.firstName,
                  lastName: rawParams.lastName,
                  isAllowedAppAccess: rawParams.isAllowedAppAccess,
                },
              }
            : caregiver,
        ),
      );
      return;
    }

    const caregiverId = res.updateLearnerCaregiverDetails?.caregiverData?.id;
    const metadata = res.updateLearnerCaregiverDetails.caregiverData?.caregivermetadata;
    if (!caregiverId || !metadata) return;

    setCaregivers((prev) =>
      prev.some((cg) => cg.id === caregiverId)
        ? prev
        : [...prev, { id: caregiverId, caregivermetadata: { ...metadata } }],
    );
  };

  const {
    formState,
    reset,
    onSubmit,
    unregister,
    watch,
    RegisteredFormSelected,
    RegisteredFormInput,
    RegisteredPhoneNumberInput,
    RegisteredCheckboxList,
    RegisteredFormRadioInput,
  } = useFormUtils<CaregiverInput & AdditionalProperty>({
    useNativeValidation: false,
    mutationFn: async (params) => {
      const { isExistingCaregiver: _, ...rawParams } = params;

      if (clientId) {
        const res = await updateLearnerCaregiverDetailsMutation.mutateAsync({
          caregiverDetails: {
            ...rawParams,
            caregiverId: caregiver ? caregiver.caregiverId : rawParams.caregiverId,
            smsAgreement: rawParams.smsAgreement === ("yes" as unknown as boolean) || rawParams.smsAgreement,
            isPrimary: rawParams.isPrimary === ("yes" as unknown as boolean) || rawParams.isPrimary,
            isAllowedAppAccess:
              rawParams.isAllowedAppAccess === ("yes" as unknown as boolean) || rawParams.isAllowedAppAccess,
          },
          learner: clientId ? clientId : "",
        });
        syncCaregiversAfterUpdate(res, rawParams);
      }
      if (onAddCaregiver) {
        // An untouched, optional text input submits "" rather than omitting the key —
        // only relevant here, since this is the create path (no existing value to clear).
        onAddCaregiver({
          ...rawParams,
          phoneNumber1: rawParams.phoneNumber1 || undefined,
          caregiverId: caregiver?.caregiverId ?? rawParams.caregiverId,
        } as unknown as CaregiverInput);
      }

      setDialogOpen(false);
    },
  });

  useEffect(() => {
    if (selectedCaregiver) {
      setCaregiver(selectedCaregiver);
    }
  }, [selectedCaregiver]);

  const isExistingCaregiver = watch("isExistingCaregiver");
  const selectedCaregiverID = isExistingCaregiver === "yes" ? watch("caregiverId") : null;

  // Leaving the "Yes" path needs to drop caregiverId's value AND its required rule —
  // otherwise a stale/empty value from that path blocks submission after switching back to "No".
  useEffect(() => {
    if (isExistingCaregiver !== "yes") {
      unregister("caregiverId");
    }
  }, [isExistingCaregiver, unregister]);

  useEffect(() => {
    if (selectedCaregiverID) {
      const availableCaregiver = caregivers.find((caregiver) => caregiver.id === selectedCaregiverID);
      if (availableCaregiver) {
        setCaregiver({
          caregiverId: availableCaregiver?.id,
          firstName: availableCaregiver.caregivermetadata?.firstName,
          lastName: availableCaregiver.caregivermetadata?.lastName,
          email: availableCaregiver.caregivermetadata?.email,
          nearestLocation: availableCaregiver.caregivermetadata?.nearestLocation,
          phoneNumber1: availableCaregiver.caregivermetadata?.phoneNumber1,
          referralSource: availableCaregiver.caregivermetadata?.referralSource,
          // @ts-ignore: Ignoring the compiler and risking bugs because: see above
          smsAgreement: availableCaregiver.caregivermetadata?.smsAgreement ? "yes" : undefined,
          // @ts-ignore: Ignoring the compiler and risking bugs because: boolean typecasting
          isPrimary: isPrimaryCaregiver ? "yes" : undefined,
          // @ts-ignore: Ignoring the compiler and risking bugs because: boolean typecasting
          isAllowedAppAccess: availableCaregiver.caregivermetadata?.isAllowedAppAccess ? "yes" : undefined,
        } as unknown as CaregiverInput);
      }
    }
  }, [selectedCaregiverID, unregister, isPrimaryCaregiver]);

  useEffect(() => {
    if (caregiver) {
      reset({
        isExistingCaregiver: selectedCaregiver ? undefined : caregiver.caregiverId ? "yes" : "no",
        caregiverId: caregiver.caregiverId,
        firstName: caregiver.firstName,
        lastName: caregiver.lastName,
        email: caregiver.email,
        nearestLocation: caregiver.nearestLocation,
        phoneNumber1: caregiver.phoneNumber1,
        referralSource: caregiver.referralSource,
        smsAgreement: caregiver.smsAgreement,
        isPrimary: caregiver.isPrimary,
        isAllowedAppAccess: caregiver.isAllowedAppAccess,
      });
    } else {
      reset({
        isExistingCaregiver: "no",
        // @ts-ignore: Ignoring the compiler and risking bugs because: boolean typecasting
        isPrimary: isPrimaryCaregiver ? "yes" : undefined,
        // quickAdd hides this control, so default to no access rather than
        // silently granting it with no visible choice.
        isAllowedAppAccess: variant !== "quickAdd",
      });
    }
  }, [caregiver, isPrimaryCaregiver, reset]);

  const disableFields = isExistingCaregiver === "yes" && !selectedCaregiver;
  // While adding via the "Existing Caregiver? Yes" path, hold off on showing caregiver
  // info until an actual selection is made — nothing to disclose before that.
  const showCaregiverFields = !!selectedCaregiver || isExistingCaregiver !== "yes" || !isNil(selectedCaregiverID);

  // RHF doesn't unregister fields on conditional unmount by default — without this, a
  // value typed while these fields were visible can still be submitted after they're hidden.
  // keepDefaultValue preserves the isPrimary/isAllowedAppAccess defaults set by the reset()
  // above, so toggling Existing Caregiver to "Yes" and back to "No" doesn't wipe them out.
  useEffect(() => {
    if (!showCaregiverFields) {
      unregister(
        [
          "firstName",
          "lastName",
          "email",
          "phoneNumber1",
          "nearestLocation",
          "referralSource",
          "smsAgreement",
          "isAllowedAppAccess",
          "isPrimary",
        ],
        { keepDefaultValue: true },
      );
    }
  }, [showCaregiverFields, unregister]);

  return (
    <FormContainer onSubmit={onSubmit}>
      <Dialog
        title={selectedCaregiver ? "View or Edit Caregiver Info" : "Add a caregiver"}
        open={isDialogOpen}
        setOpen={setDialogOpen}
        primaryButton={{
          text: selectedCaregiver ? "Update" : "Add",
          onClick: onSubmit,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => {
            setDialogOpen(false);
          },
        }}
        size="xl"
        overflowBehavior="scroll"
      >
        <div className="col-span-3 p-4 flex flex-col">
          {!selectedCaregiver ? (
            <DoubleColumnContainer>
              <RegisteredFormRadioInput
                formState={formState}
                formKey="isExistingCaregiver"
                title="Existing Caregiver?"
                items={[
                  {
                    id: "no",
                    title: "No",
                  },
                  {
                    id: "yes",
                    title: "Yes",
                  },
                ]}
                required={selectedCaregiver || caregiver?.caregiverId ? false : true}
              />
            </DoubleColumnContainer>
          ) : null}
          {isExistingCaregiver === "yes" && (
            <div className="ph-no-capture">
              <RegisteredFormSelected
                formKey="caregiverId"
                formState={formState}
                placeholderText={""}
                title="Select Existing Caregiver"
                isLoading={isCaregiverLoading}
                items={
                  caregivers
                    .filter(
                      (caregiver) => !assignedCaregivers.some((assigned) => assigned.caregiverId === caregiver.id),
                    )
                    .sort((a, b) => (a.caregivermetadata?.firstName > b.caregivermetadata?.firstName ? 1 : -1))
                    .map((o) => ({
                      primary: `${o?.caregivermetadata?.firstName} ${o?.caregivermetadata?.lastName}`,
                      secondary: o.caregivermetadata?.email,
                      id: o?.id,
                      className: "ph-no-capture",
                    })) as unknown as SelectItem[]
                }
              />
            </div>
          )}
          {showCaregiverFields && (
            <>
              <div className="gap-x-4  lg:grid lg:grid-cols-2">
                <RegisteredFormInput
                  formKey="firstName"
                  inputSize="full"
                  formState={formState}
                  label="First Name"
                  disabled={disableFields}
                  readOnly={!isNil(selectedCaregiverID)}
                />
                <RegisteredFormInput
                  formKey="lastName"
                  inputSize="full"
                  formState={formState}
                  label="Last Name"
                  disabled={disableFields}
                  readOnly={!isNil(selectedCaregiverID)}
                />
              </div>

              <RegisteredFormInput
                formKey="email"
                inputSize="full"
                formState={formState}
                label="Email Address"
                readOnly={!isNil(selectedCaregiverID)}
                disabled={disableFields || !isNil(selectedCaregiver?.intakeInvite)}
              />
              <RegisteredPhoneNumberInput
                formKey="phoneNumber1"
                formState={formState}
                defaultCountry="US"
                label="Phone Number"
                readOnly={!isNil(selectedCaregiverID)}
                required={false}
                disabled={disableFields}
              />
              {isOnboardingEnabled ? (
                <>
                  <RegisteredFormSelected
                    formKey="nearestLocation"
                    formState={formState}
                    placeholderText={""}
                    title="Nearest Location"
                    errorMessage={formState.errors.nearestLocation?.message}
                    items={NEAREST_LOCATIONS_OPTIONS.map((o) => ({ primary: o }))}
                    readOnly={!isNil(selectedCaregiverID)}
                    disabled={disableFields}
                    required={false}
                  />
                  <RegisteredFormSelected
                    formKey="referralSource"
                    formState={formState}
                    placeholderText={""}
                    required={false}
                    title="Referral Source"
                    items={REFERRAL_SOURCE_OPTIONS.map((o) => ({ primary: o }))}
                    readOnly={!isNil(selectedCaregiverID)}
                    disabled={disableFields}
                  />
                  <RegisteredCheckboxList
                    formKey="smsAgreement"
                    formState={formState}
                    required={false}
                    title="SMS Agreement"
                    items={SMSAgreementItem}
                    readOnly={!isNil(selectedCaregiverID)}
                    disabled={disableFields}
                  />
                </>
              ) : null}
              {variant !== "quickAdd" && (
                <>
                  <div className="flex flex-row gap-1">
                    <RegisteredCheckboxList
                      formKey="isAllowedAppAccess"
                      formState={formState}
                      required={false}
                      title=""
                      items={IsAllowedAppAccessItem}
                      readOnly={!isNil(selectedCaregiverID)}
                    />
                    <div className="mt-4">
                      <Tooltip text="Checking this box will allow this caregiver to access the Frontera app for recording videos of their child."></Tooltip>
                    </div>
                  </div>
                  <div className="flex flex-row gap-1">
                    <RegisteredCheckboxList
                      formKey="isPrimary"
                      formState={formState}
                      required={false}
                      title=""
                      items={PrimaryCaregiverItem}
                      readOnly={!isNil(selectedCaregiverID)}
                      disabled={!!isPrimaryCaregiver}
                    />
                    <div className="mt-4">
                      <Tooltip text="Only one primary caregiver can be assigned per client. This caregiver will be the main contact for communication for the client."></Tooltip>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </Dialog>
    </FormContainer>
  );
};
